import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId, type PurchaseOrder, type Supply } from '../api';
import { CampoDeVencimiento } from '../CampoDeVencimiento';
import { Boton, Pastilla } from '../componentes';
import { cantidad, contados, soles } from '../formato';
import { costoPorUnidadDeStock, leerCantidad, leerImporte, problemaDeCantidad } from '../numeros';
import { fechaDeCalendario } from '../vencimiento';
import { RecepcionPorSerie } from './RecepcionPorSerie';

/** Lo que se va a recibir de una línea de la orden. */
interface FilaARecibir {
  va: boolean;
  cuanto: string;
  costo: string;
  lote: string;
  vence: string;
  sinFecha: boolean;
}

/** Lo que pasó al recibir una línea. */
type Resultado =
  | {
      ok: true;
      lote: { code: string; expiresAt: string | null; addedToExisting: boolean } | null;
    }
  | { ok: false; mensaje: string };

/** El precio pactado como se teclea: `38,5` y no `38.5000`. */
function comoSeTeclea(numero: string): string {
  const valor = Number.parseFloat(numero);
  return Number.isNaN(valor) ? '' : String(Math.round(valor * 10_000) / 10_000).replace('.', ',');
}

/**
 * Recibir contra una orden de compra: todo lo que falta, de una vez.
 *
 * Antes elegir la orden no cambiaba nada —estaba al final del formulario— y un
 * camión con diez cosas eran diez envíos, tecleando otra vez cantidades y
 * precios que la orden ya sabía. Aquí salen las líneas que faltan, con lo que
 * falta y el precio pactado ya puestos: se corrige solo lo que llegó distinto,
 * y lo que no llegó se desmarca.
 *
 * Cada línea es su propia entrada, con su identificador fijo mientras la
 * pantalla siga abierta: si la red se corta a la mitad, volver a pulsar termina
 * las que faltaban y no repite las que ya entraron.
 */
export function RecibirOrden({
  orden,
  insumos,
  onOtraVez,
}: {
  orden: PurchaseOrder;
  insumos: Supply[];
  /** Vuelve a abrir la orden con lo que le falta ahora. */
  onOtraVez: () => void;
}) {
  const clientes = useQueryClient();
  // Si el negocio exige el vencimiento de cada lote (la botica).
  const ajustesDeLotes = useQuery({
    queryKey: ['ajustes-de-lotes'],
    queryFn: () => almacen.lotSettings(),
    retry: false,
  });
  /*
   * Las líneas, fijas desde que se abrió. Al recibir se vuelve a pedir la
   * orden, y con la de ahora la línea que llegó entera desaparecía de la lista
   * —con su lote y su resultado— y la que llegó a medias se quedaba sin forma
   * de recibir el resto. Lo que aún falta se abre aparte, con «Recibir lo que
   * aún falta».
   */
  const [pendientes] = useState(() =>
    orden.lines.filter((linea) => Number.parseFloat(linea.pending) > 0),
  );
  const faltaAhora = orden.lines.filter((linea) => Number.parseFloat(linea.pending) > 0).length;
  const fichaDe = (supplyId: string) => insumos.find((insumo) => insumo.id === supplyId);

  const [filas, setFilas] = useState<Record<string, FilaARecibir>>(() =>
    Object.fromEntries(
      pendientes.map((linea) => [
        linea.supplyId,
        {
          va: true,
          cuanto: comoSeTeclea(linea.pending),
          costo: comoSeTeclea(linea.unitCost),
          lote: '',
          vence: '',
          sinFecha: false,
        },
      ]),
    ),
  );
  const [guia, setGuia] = useState('');
  const [operaciones] = useState(() => new Map<string, string>());
  const [resultados, setResultados] = useState<Record<string, Resultado>>({});
  const [recibiendo, setRecibiendo] = useState(false);

  const cambiar = (supplyId: string, cambio: Partial<FilaARecibir>) =>
    setFilas((antes) => ({ ...antes, [supplyId]: { ...antes[supplyId]!, ...cambio } }));

  const yaEntro = (supplyId: string) => resultados[supplyId]?.ok === true;

  /**
   * Donde el negocio exige el vencimiento (la botica), el lote es el que trae
   * la caja y no se numera solo: lo exige también el servidor
   * (`LOT_CODE_REQUIRED`).
   */
  const exigeLoteDelFabricante = (supplyId: string): boolean =>
    (fichaDe(supplyId)?.isLotTracked ?? false) && (ajustesDeLotes.data?.requiresLotExpiry ?? false);

  /** Lo que le falta a una fila para poder entrar, o nulo si está lista. */
  const queLeFalta = (supplyId: string): string | null => {
    const fila = filas[supplyId]!;
    const problema = problemaDeCantidad(fila.cuanto, fichaDe(supplyId)?.unit);
    if (problema !== null) return problema;
    if (Number.isNaN(leerCantidad(fila.cuanto)) || leerCantidad(fila.cuanto) <= 0) {
      return 'Falta cuánto llegó';
    }
    if (Number.isNaN(leerImporte(fila.costo)) || leerImporte(fila.costo) < 0) {
      return 'Falta el precio';
    }
    if ((fichaDe(supplyId)?.isLotTracked ?? false) && fila.vence === '' && !fila.sinFecha) {
      return 'Falta el vencimiento';
    }
    if (exigeLoteDelFabricante(supplyId) && fila.lote.trim() === '') {
      return 'Falta el lote de la caja';
    }
    return null;
  };

  /** Lo que va por serie entra escaneando cada caja, no con una cantidad. */
  const porSerie = (supplyId: string): boolean => fichaDe(supplyId)?.isSerialTracked ?? false;

  const porRecibir = pendientes.filter(
    (linea) => filas[linea.supplyId]!.va && !yaEntro(linea.supplyId) && !porSerie(linea.supplyId),
  );
  const listas = porRecibir.filter((linea) => queLeFalta(linea.supplyId) === null);

  const recibir = async () => {
    setRecibiendo(true);
    const nuevos: Record<string, Resultado> = {};

    // De a una y en orden: un error en la tercera no deshace las dos primeras,
    // que ya están en el estante, y se dice cuál falló.
    for (const linea of listas) {
      const fila = filas[linea.supplyId]!;
      const pideLote = fichaDe(linea.supplyId)?.isLotTracked ?? false;
      let operationId = operaciones.get(linea.supplyId);
      if (operationId === undefined) {
        operationId = newOperationId();
        operaciones.set(linea.supplyId, operationId);
      }

      try {
        const respuesta = await almacen.receive({
          operationId,
          supplyId: linea.supplyId,
          quantity: leerCantidad(fila.cuanto).toFixed(3),
          // `queLeFalta` ya comprobó que es un importe.
          unitCost: costoPorUnidadDeStock(fila.costo)!,
          purchaseOrderId: orden.id,
          ...(pideLote && fila.lote.trim() !== '' ? { lotCode: fila.lote.trim() } : {}),
          ...(pideLote && fila.vence !== '' ? { expiresAt: fila.vence } : {}),
          ...(guia.trim() === '' ? {} : { document: guia.trim() }),
        }, (mensaje) => window.confirm(mensaje));
        nuevos[linea.supplyId] = { ok: true, lote: respuesta.lot ?? null };
      } catch (error) {
        nuevos[linea.supplyId] = {
          ok: false,
          mensaje: error instanceof Error ? error.message : 'No se pudo recibir.',
        };
      }
    }

    setResultados((antes) => ({ ...antes, ...nuevos }));
    setRecibiendo(false);
    void clientes.invalidateQueries();
  };

  const entraron = pendientes.filter((linea) => yaEntro(linea.supplyId)).length;
  const fallaron = Object.values(resultados).filter((resultado) => !resultado.ok).length;

  if (pendientes.length === 0) {
    return (
      <p className="aviso-caja">
        A la {orden.number} no le falta nada: ya llegó todo lo que se pidió.
      </p>
    );
  }

  return (
    <div className="formulario">
      <p className="nota nota--pegada">
        {orden.supplier}
        {orden.expectedAt === null ? '' : ` · se esperaba el ${fechaDeCalendario(orden.expectedAt.slice(0, 10))}`}
        {' · '}
        {contados(pendientes.length, 'línea por llegar', 'líneas por llegar')}. Los precios
        son los pactados, sin IGV; corrige lo que llegó distinto y desmarca lo que no vino.
      </p>

      <ul className="lista recibir-orden">
        {pendientes.map((linea) => {
          const fila = filas[linea.supplyId]!;
          const ficha = fichaDe(linea.supplyId);
          const pideLote = ficha?.isLotTracked ?? false;
          const resultado = resultados[linea.supplyId];
          const cerrada = resultado?.ok === true;
          const falta = fila.va && !cerrada ? queLeFalta(linea.supplyId) : null;
          const idBase = `orden-${linea.supplyId}`;

          return (
            <li key={linea.supplyId} className="recibir-orden__fila">
              <label className="recibir-orden__cabeza">
                <input
                  type="checkbox"
                  checked={fila.va}
                  disabled={cerrada || recibiendo}
                  onChange={() => cambiar(linea.supplyId, { va: !fila.va })}
                />
                <span className="lista__texto">
                  <strong>{linea.supply}</strong>
                  <span className="apagado">
                    pedido {cantidad(linea.ordered)} · llegó {cantidad(linea.received)} · falta{' '}
                    {cantidad(linea.pending)} {linea.unit}
                  </span>
                </span>
                {cerrada ? (
                  <Pastilla texto="Recibido" tono="exito" />
                ) : !fila.va ? (
                  <Pastilla texto="No llegó" tono="neutro" />
                ) : falta !== null ? (
                  <Pastilla texto={falta} tono="aviso" />
                ) : null}
              </label>

              {fila.va && !cerrada && porSerie(linea.supplyId) && ficha !== undefined ? (
                // Un pedido de celulares: cada caja con su IMEI, contra esta
                // orden. Lo que va por cantidad sigue abajo con «Recibir».
                <RecepcionPorSerie insumo={ficha} guia={guia} purchaseOrderId={orden.id} />
              ) : fila.va && !cerrada ? (
                <div className="formulario__fila">
                  <div className="campo">
                    <label className="rotulo-simple" htmlFor={`${idBase}-cuanto`}>
                      Llegó ({linea.unit})
                    </label>
                    <input
                      id={`${idBase}-cuanto`}
                      inputMode="decimal"
                      value={fila.cuanto}
                      disabled={recibiendo}
                      onChange={(e) => cambiar(linea.supplyId, { cuanto: e.target.value })}
                    />
                  </div>
                  <div className="campo">
                    <label className="rotulo-simple" htmlFor={`${idBase}-costo`}>
                      Precio por {linea.unit}, sin IGV
                    </label>
                    <input
                      id={`${idBase}-costo`}
                      inputMode="decimal"
                      value={fila.costo}
                      disabled={recibiendo}
                      onChange={(e) => cambiar(linea.supplyId, { costo: e.target.value })}
                    />
                  </div>
                  {pideLote ? (
                    <div className="campo">
                      <label className="rotulo-simple" htmlFor={`${idBase}-lote`}>
                        {exigeLoteDelFabricante(linea.supplyId)
                          ? 'Lote del fabricante'
                          : 'Lote del proveedor (opcional)'}
                      </label>
                      <input
                        id={`${idBase}-lote`}
                        placeholder={
                          exigeLoteDelFabricante(linea.supplyId) ? 'El que trae la caja' : 'Se pone solo'
                        }
                        value={fila.lote}
                        disabled={recibiendo}
                        onChange={(e) => cambiar(linea.supplyId, { lote: e.target.value })}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {fila.va && !cerrada && pideLote && !porSerie(linea.supplyId) ? (
                <div className="campo">
                  <label className="rotulo-simple" htmlFor={`${idBase}-vence`}>
                    Vence
                  </label>
                  <CampoDeVencimiento
                    id={`${idBase}-vence`}
                    valor={fila.vence}
                    onCambio={(vence) => cambiar(linea.supplyId, { vence })}
                    sinFecha={fila.sinFecha}
                    onSinFecha={(sinFecha) => cambiar(linea.supplyId, { sinFecha })}
                    vidaUtilDias={ficha?.shelfLifeDays ?? null}
                    permiteSinFecha={
                      !(ajustesDeLotes.data?.requiresLotExpiry ?? false) && !(ficha?.requiresExpiry ?? false)
                    }
                    margenDias={ajustesDeLotes.data?.expiryAlertDays ?? 3}
                  />
                </div>
              ) : null}

              {resultado === undefined ? null : resultado.ok ? (
                resultado.lote === null ? null : (
                  <p className="nota-sm nota--pegada">
                    Entró en el lote <span className="mono">{resultado.lote.code}</span>
                    {resultado.lote.expiresAt === null
                      ? ', sin fecha de vencimiento.'
                      : `, vence el ${fechaDeCalendario(resultado.lote.expiresAt)}.`}
                    {resultado.lote.addedToExisting
                      ? ' Ese lote ya existía: se sumó a él y se quedó con su fecha.'
                      : ''}
                  </p>
                )
              ) : (
                <p className="aviso-caja aviso-caja--peligro" role="alert">
                  No entró: {resultado.mensaje}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      <div className="campo">
        <label className="rotulo-simple" htmlFor="orden-guia">
          Guía de remisión
        </label>
        <input
          id="orden-guia"
          placeholder="GR-44821"
          value={guia}
          disabled={recibiendo}
          onChange={(e) => setGuia(e.target.value)}
        />
      </div>

      {entraron > 0 ? (
        <p className="aviso-caja" role="status">
          {contados(entraron, 'línea recibida', 'líneas recibidas')} contra la {orden.number}
          {fallaron > 0 ? `; ${contados(fallaron, 'falló', 'fallaron')}: corrígela y vuelve a recibir.` : '.'}{' '}
          Ya están en el estante y en el kardex con tu nombre.
        </p>
      ) : null}

      {porRecibir.length === 0 && entraron > 0 && faltaAhora > 0 ? (
        <Boton onClick={onOtraVez}>
          Recibir lo que aún falta ({contados(faltaAhora, 'línea', 'líneas')})
        </Boton>
      ) : null}

      <Boton
        variante="primario"
        onClick={() => void recibir()}
        disabled={listas.length === 0 || recibiendo}
      >
        {recibiendo
          ? 'Recibiendo…'
          : listas.length === 0
            ? porRecibir.length === 0
              ? 'Nada más por recibir'
              : 'Completa las líneas marcadas'
            : `Recibir ${contados(listas.length, 'línea', 'líneas')} · ${soles(
                listas.reduce(
                  (suma, linea) =>
                    suma +
                    leerCantidad(filas[linea.supplyId]!.cuanto) *
                      leerImporte(filas[linea.supplyId]!.costo),
                  0,
                ),
              )}`}
      </Boton>
    </div>
  );
}
